import styled from 'styled-components';

import { COLORS, HOME, HOME_NARROW, MOBILE, SPACE } from '../../theme/tokens';
import { Divider, Text } from '../../components';

/** The tab's column: header, summary, figures, chart and lists, one gap apart (the Stats tab's rhythm). */
export const Page = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px; /* off-scale on purpose: matches the Stats tab's card gap so the two tabs line up */
`;

/** The four headline figures: four across, two across on narrower screens. */
export const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px; /* the Stats card gap, see Page */

  ${HOME_NARROW} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

/** Traffic and pages (two thirds) beside devices and systems (a third). One column below the narrow-desktop width,
 *  where a third is too slim for the tiles; the DOM order keeps each group together there. */
export const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 10px; /* the Stats card gap, see Page */
  align-items: start;

  ${HOME_NARROW} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px; /* the Stats card gap, see Page */
  min-width: 0;
`;

export const Footnote = styled(Text)<{ $readable?: boolean }>`
  display: block;
  padding: ${SPACE[4]} ${SPACE[8]} ${SPACE[16]};
  font-size: 13px;
  line-height: 1.5;
  color: ${(p) => (p.$readable ? COLORS.textMuted : HOME.t500)};
`;

/** The header's pickers (page, period), side by side at the row's right. On a phone (where the title is dropped)
 *  they take the whole row on one line: the page pill gives way (its name ellipsed), the period keeps its width, and
 *  the page panel opens across the row. `$wrap` (the source and link pickers too): on a phone they wrap onto more
 *  lines instead. */
export const Controls = styled.div<{ $wrap?: boolean }>`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: ${SPACE[8]};
  margin-left: auto;

  ${MOBILE} {
    flex: 1 1 auto;
    flex-wrap: ${(p) => (p.$wrap ? 'wrap' : 'nowrap')};
    min-width: 0;

    > :first-child {
      flex: 0 1 auto;
      min-width: 0;
    }
    > :last-child {
      flex: none;
    }
  }
`;

/** The faded hairline between two parts of one card (devices, then systems). */
export const CardDivider = styled(Divider)`
  margin: ${SPACE[12]} ${SPACE[0]} ${SPACE[4]};
`;

/** A second heading inside a card, set like the card's own title (StatCard's head). */
export const CardSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  padding: 6px 5px 4px; /* off-scale on purpose: StatCard's head inset, so both headings line up */
`;

export const CardSectionTitle = styled(Text)`
  margin: ${SPACE[0]};
  font-size: 18px;
  font-weight: 700;
  color: ${HOME.t300};
`;

export const CardSectionSub = styled(Text)`
  font-size: 14px;
  font-weight: 300;
  color: ${HOME.t300};
`;

/** The overview card's buttons at its top right (the data notes, then the report download), side by side. */
export const SummaryActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: ${SPACE[8]};
`;

/** The quiet line under the figures saying how old they are. */
export const UpdatedLine = styled(Text)`
  display: block;
  font-size: 13px;
  line-height: 1.5;
  color: ${HOME.t500};
`;

/** Where the figures would be while no address is counted: the note's one action. */
export const NoteAction = styled.div`
  display: flex;
  justify-content: flex-start;
`;
